import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2, Image, Type, Eye, Upload } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminHomeSection = () => {
  const [data, setData] = useState({
    heroTitle: '', heroSubtitle: '', heroDescription: '',
    heroImage: '', bookingCta: '', salonPhone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/site-texts`);
      setData({
        heroTitle: response.data.heroTitle || '',
        heroSubtitle: response.data.heroSubtitle || '',
        heroDescription: response.data.heroDescription || '',
        heroImage: response.data.heroImage || '',
        bookingCta: response.data.bookingCta || '',
        salonPhone: response.data.salonPhone || '',
      });
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-texts`, data, { headers: getAuthHeaders() });
      toast.success('Home aggiornata!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setData({ ...data, heroImage: `${BACKEND_URL}${res.data.url}` });
      toast.success('Immagine caricata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="home-section">
      <h2 className="text-xl sm:text-2xl font-bold">Modifica Home</h2>

      <Card className="card-elegant">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" /> Immagine Hero</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <input type="file" accept="image/*" ref={fileRef} onChange={handleUploadHero} className="hidden" data-testid="hero-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex-1" data-testid="hero-upload-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Caricamento...</> : <><Upload className="h-4 w-4 mr-2" /> Carica immagine</>}
            </Button>
          </div>
          <div>
            <Label>Oppure incolla URL</Label>
            <Input
              value={data.heroImage}
              onChange={(e) => setData({ ...data, heroImage: e.target.value })}
              placeholder="https://esempio.com/immagine.jpg"
              className="text-base"
              data-testid="hero-image-input"
            />
          </div>
          {data.heroImage && (
            <div>
              <Button variant="outline" size="sm" onClick={() => setPreviewImage(!previewImage)} className="mb-2" data-testid="preview-image-btn">
                <Eye className="h-3 w-3 mr-1" /> {previewImage ? 'Nascondi' : 'Anteprima'}
              </Button>
              {previewImage && (
                <div className="relative rounded-lg overflow-hidden h-40 sm:h-56 border border-border">
                  <img src={data.heroImage} alt="Anteprima" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70 flex items-center justify-center">
                    <p className="text-white text-xl font-bold">{data.heroTitle || 'Titolo'}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-elegant">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Type className="h-4 w-4" /> Testi Hero</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Titolo Principale</Label>
            <Input value={data.heroTitle} onChange={(e) => setData({ ...data, heroTitle: e.target.value })} placeholder="CAPELLI & CAPELLI" className="text-base" data-testid="hero-title-input" />
          </div>
          <div>
            <Label>Sottotitolo</Label>
            <Input value={data.heroSubtitle} onChange={(e) => setData({ ...data, heroSubtitle: e.target.value })} placeholder="Di Evian - Sala Uomo" className="text-base" data-testid="hero-subtitle-input" />
          </div>
          <div>
            <Label>Descrizione</Label>
            <Textarea value={data.heroDescription} onChange={(e) => setData({ ...data, heroDescription: e.target.value })} placeholder="Stile, precisione e personalita" rows={2} className="text-base" data-testid="hero-description-input" />
          </div>
          <div>
            <Label>Testo Pulsante</Label>
            <Input value={data.bookingCta} onChange={(e) => setData({ ...data, bookingCta: e.target.value })} placeholder="PRENOTA ORA" className="text-base" data-testid="hero-cta-input" />
          </div>
          <div>
            <Label>Telefono Salone (per lista d'attesa)</Label>
            <Input value={data.salonPhone} onChange={(e) => setData({ ...data, salonPhone: e.target.value })} placeholder="+39 xxx xxx xxxx" className="text-base" data-testid="salon-phone-input" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full sm:w-auto" data-testid="save-home-btn">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</> : <><Save className="mr-2 h-4 w-4" /> Salva Modifiche</>}
        </Button>
      </div>
    </div>
  );
};

export default AdminHomeSection;
